import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNewspaper, faPlus, faCog, faEllipsisV, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import CommunityPostCard from './CommunityPostCard';
import CreatePostModal from './CreatePostModal';
import PostTagModal from '../modals/PostTagModal';
import DeleteConfirmationModal from '../modals/DeleteConfirmationModal';
import groupService from '../../api/groupService';
import { AdminPageLayout, AdminPageFilters } from '../common';
import '../../styles/FinancialsPage.css'; // For chip filter styles
import '../../styles/group/UpdatesList.css'; // For posts grid layout

// Default tag options for when no custom tags exist
const defaultTagOptions = [
  { value: 'Announcement', label: 'Announcement', icon: '📢', color: '#FF6B6B' },
  { value: 'Update', label: 'Update', icon: '📰', color: '#5669FF' },
  { value: 'Mazal Tov', label: 'Mazal Tov', icon: '🎉', color: '#FFD700' }
];

const UpdatesList = ({ groupId, searchQuery, refreshTrigger, isAdmin }) => {
  const [posts, setPosts] = useState([]);
  const [postTags, setPostTags] = useState([]); // Custom tags from database
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasApiError, setHasApiError] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null); // Post being edited
  const [activeTag, setActiveTag] = useState('all'); // 'all' or specific tag
  const [sortBy, setSortBy] = useState('newest');
  
  // Tag management states
  const [showTagModal, setShowTagModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [showDeleteTagModal, setShowDeleteTagModal] = useState(false);
  const [deletingTag, setDeletingTag] = useState(null);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [openTagDropdownId, setOpenTagDropdownId] = useState(null);

  // Fetch posts and tags function - extracted to be reusable
  const fetchData = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setHasApiError(false);
      
      console.log('🔄 Fetching data for groupId:', groupId);
      
      // Fetch both posts and tags in parallel
      const [postsResponse, tagsResponse] = await Promise.all([
        groupService.getCommunityPosts(groupId),
        groupService.getPostTags(groupId)
      ]);
      
      console.log('📊 Posts Response:', postsResponse);
      console.log('🏷️ Tags Response:', tagsResponse);
      console.log('🏷️ Tags Response.success:', tagsResponse.success);
      console.log('🏷️ Tags Response.data:', tagsResponse.data);
      console.log('🏷️ Tags Response.data type:', typeof tagsResponse.data);
      console.log('🏷️ Tags Response.data is array?', Array.isArray(tagsResponse.data));
      
      const postsData = Array.isArray(postsResponse.data) ? postsResponse.data : (postsResponse.data?.data || []);
      setPosts(postsData);
      
      const tagsData = tagsResponse.success ? (tagsResponse.data || []) : [];
      console.log('🏷️ Setting tagsData to:', tagsData);
      console.log('🏷️ tagsData length:', tagsData.length);
      setPostTags(tagsData);
      
    } catch (err) {
      console.error('Error fetching community updates data:', err);
      setError('Failed to load community updates. Please try again.');
      setHasApiError(true);
      setPosts([]);
      setPostTags([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Fetch posts for this group
  useEffect(() => {
    if (groupId) {
      fetchData();
    }
  }, [groupId, refreshTrigger]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openTagDropdownId !== null && !event.target.closest('.product-type-chip-container')) {
        setOpenTagDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openTagDropdownId]);

  // Get unique tags from actual posts (for filtering)
  const uniquePostTags = React.useMemo(() => {
    console.log('🔧 Computing uniquePostTags...');
    console.log('🔧 Current postTags state:', postTags);
    console.log('🔧 Current posts:', posts.length);
    
    const tagCounts = {};
    posts.forEach(post => {
      if (post.tag) {
        tagCounts[post.tag] = (tagCounts[post.tag] || 0) + 1;
      }
    });
    
    console.log('🔧 Tag counts from posts:', tagCounts);
    
    // Start with default tags (always show as filters)
    const defaultFilters = defaultTagOptions.map(defaultTag => ({
      value: defaultTag.value,
      label: defaultTag.label,
      icon: defaultTag.icon,
      color: defaultTag.color,
      count: tagCounts[defaultTag.value] || 0
    }));
    
    console.log('🔧 Default filters:', defaultFilters);
    
    // Add custom tags from posts that aren't in defaults
    const customFilters = Object.keys(tagCounts)
      .filter(tagName => !defaultTagOptions.some(dt => dt.value === tagName))
      .map(tagName => {
        // Try to find matching tag in library for icon/color
        const libraryTag = postTags.find(t => t.name === tagName);
        console.log(`🔧 Looking for tag "${tagName}" in library:`, libraryTag);
        return {
          value: tagName,
          label: tagName,
          icon: libraryTag?.icon || '📝',
          color: libraryTag?.color || '#5669FF',
          count: tagCounts[tagName]
        };
      })
      .sort((a, b) => a.value.localeCompare(b.value));
    
    console.log('🔧 Custom filters from posts:', customFilters);
    
    // Combine defaults first, then custom tags
    const result = [...defaultFilters, ...customFilters];
    console.log('🔧 Final uniquePostTags result:', result);
    return result;
  }, [posts, postTags]);

  // Tag library for dropdown in create post (combines library + defaults)
  const tagLibraryOptions = postTags.length > 0 
    ? postTags.map(tag => ({
        value: tag.name,
        label: tag.name,
        icon: tag.icon,
        color: tag.color,
        id: tag.id
      }))
    : defaultTagOptions;

  // Filter and sort posts - only by tag
  const filteredPosts = posts.filter(post => {
    // Tag filter
    if (activeTag !== 'all') {
      return post.tag === activeTag;
    }
    
    return true;
  });

  const sortedPosts = [...filteredPosts].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'oldest':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'most_liked':
        return (b.likes_count || 0) - (a.likes_count || 0);
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  // Handle post creation
  const handlePostCreated = (newPost) => {
    // Check if this is just a tag refresh request
    if (newPost && newPost.justRefreshTags) {
      // Refresh tags without affecting posts
      fetchData(false);
      return;
    }
    
    setPosts(prevPosts => [newPost, ...prevPosts]);
    setShowCreateModal(false);
  };

  // Handle post deleted
  const handlePostDeleted = (deletedPostId) => {
    // Immediate UI update
    setPosts(prevPosts => prevPosts.filter(post => post.id !== deletedPostId));
    
    // Refetch to ensure data consistency
    setTimeout(() => {
      fetchData(false); // Don't show loading spinner for background refresh
    }, 100);
  };

  // Handle tag created/updated
  const handleTagSaved = (savedTag) => {
    setPostTags(prev => {
      const existingIndex = prev.findIndex(t => t.id === savedTag.id);
      if (existingIndex >= 0) {
        // Update existing tag
        const updated = [...prev];
        const oldTagName = updated[existingIndex].name;
        updated[existingIndex] = savedTag;
        
        // If tag name changed, update all posts using this tag
        if (oldTagName !== savedTag.name) {
          setPosts(prevPosts => 
            prevPosts.map(post => 
              post.tag === oldTagName ? { ...post, tag: savedTag.name } : post
            )
          );
        }
        
        return updated;
      } else {
        // Add new tag
        return [...prev, savedTag];
      }
    });
    
    setShowTagModal(false);
    setEditingTag(null);
    setOpenTagDropdownId(null); // Close any open dropdown menus
  };

  // Handle tag deletion
  const handleDeleteTag = async () => {
    if (!deletingTag) return;
    
    try {
      const response = await groupService.deletePostTag(groupId, deletingTag.id);
      if (response.success) {
        // Remove tag from state immediately
        setPostTags(prev => prev.filter(t => t.id !== deletingTag.id));
        setShowDeleteTagModal(false);
        setDeletingTag(null);
        setOpenTagDropdownId(null); // Close any open dropdown menus
        
        // If the deleted tag was active, reset to 'all'
        if (activeTag === deletingTag.name) {
          setActiveTag('all');
        }
      }
    } catch (err) {
      console.error('Error deleting tag:', err);
      alert('Failed to delete tag. Please try again.');
    }
  };

  // Handle post edit
  const handlePostEdit = (post) => {
    console.log('🔍 Edit post:', post);
    setEditingPost(post);
    setShowCreateModal(true);
  };

  // Handle post update after edit
  const handlePostUpdated = (updatedPost) => {
    console.log('🔄 handlePostUpdated called with:', updatedPost);
    console.log('🔄 Current posts before update:', posts.length, 'posts');
    console.log('🔄 Looking for post ID:', updatedPost?.id);
    
    // Immediate UI update
    setPosts(prevPosts => {
      const updated = prevPosts.map(post => 
        post.id === updatedPost.id ? updatedPost : post
      );
      console.log('🔄 Posts after update:', updated.length, 'posts');
      return updated;
    });
    
    setShowCreateModal(false);
    setEditingPost(null);
    
    // Refetch to ensure data consistency
    setTimeout(() => {
      fetchData(false); // Don't show loading spinner for background refresh
    }, 100);
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditingPost(null);
  };

    // Render chip-style filters (matching financials page design)
  const renderFilters = () => (
    <>
      <div className="chip-filters-container">
        <div className="chip-filters">
          <button
            className={`chip-filter ${activeTag === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTag('all')}
          >
            All
          </button>
          {uniquePostTags.map(tag => {
            // Check if this is a hardcoded default tag (cannot be edited/deleted)
            const isDefaultTag = defaultTagOptions.some(dt => dt.value === tag.value);
            // Check if tag exists in custom library (can be edited/deleted)
            const customTag = postTags.find(t => t.name === tag.value);
            // Check if tag is being used by any posts
            const isTagInUse = tag.count > 0;
            
            return (
            <div key={tag.value} className="product-type-chip-container">
              <button
                className={`chip-filter ${activeTag === tag.value ? 'active' : ''} ${customTag && !isDefaultTag && !isTagInUse ? 'chip-with-menu' : ''}`}
                onClick={() => setActiveTag(tag.value)}
              >
                <span>{tag.label}</span>
                {/* Only show menu for custom tags that are not in use and not hardcoded defaults */}
                {customTag && !isDefaultTag && !isTagInUse && (
                  <div className="chip-menu-container">
                    <div
                      className="chip-menu-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenTagDropdownId(openTagDropdownId === tag.value ? null : tag.value);
                      }}
                      title="More options"
                      role="button"
                      tabIndex={0}
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </div>
                    {openTagDropdownId === tag.value && (
                      <div className="chip-dropdown-menu">
                        <button
                          className="chip-dropdown-item"
                          onClick={() => {
                            setEditingTag(customTag);
                            setShowTagModal(true);
                            setOpenTagDropdownId(null);
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                          Edit
                        </button>
                        <button
                          className="chip-dropdown-item delete"
                          onClick={() => {
                            setDeletingTag(customTag);
                            setShowDeleteTagModal(true);
                            setOpenTagDropdownId(null);
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </button>
            </div>
            );
          })}
        </div>
        
        {/* Right side controls - Sort and New Post button */}
        <div className="right-controls">
          <AdminPageFilters.SortDropdown
            value={sortBy}
            onChange={setSortBy}
            options={[
              { value: 'newest', label: 'Newest' },
              { value: 'oldest', label: 'Oldest' },
              { value: 'most_liked', label: 'Most Liked' }
            ]}
          />
          
          {isAdmin && (
            <button 
              className="btn btn-primary"
              data-tour="new-update-button"
              onClick={() => {
                console.log('🔍 Create Post button clicked');
                setShowCreateModal(true);
              }}
              style={{
                backgroundColor: '#5669FF',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
              New Post
            </button>
          )}
        </div>
      </div>
    </>
  );

  // Render empty state
  const renderEmptyState = () => {
    if (hasApiError) {
      return (
        <div className="empty-state error-state">
          <FontAwesomeIcon icon={faNewspaper} className="empty-icon error" />
          <h3>Unable to Load Community Updates</h3>
          <p>There was an error loading the community updates. Please check your connection and try again.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="retry-button"
          >
            Try Again
          </button>
        </div>
      );
    }

    if (activeTag !== 'all' && posts.length > 0) {
      const selectedTag = uniquePostTags.find(tag => tag.value === activeTag);
      return (
        <div className="empty-state">
          <FontAwesomeIcon icon={faNewspaper} className="empty-icon" />
          <h3>No {selectedTag?.label || 'Posts'} Found</h3>
          <p>There are no {selectedTag?.label.toLowerCase() || 'posts'} at the moment. Posts with this tag will appear here when created.</p>
        </div>
      );
    }

    return (
      <div className="empty-state">
        <FontAwesomeIcon icon={faNewspaper} className="empty-icon" />
        <h3>Share Your First Update</h3>
        <p>Start building your community by sharing posts, announcements, and moments that matter.</p>
        {isAdmin && (
          <button 
            onClick={() => {
              console.log('🔍 Create First Post button clicked');
              setShowCreateModal(true);
            }}
            className="create-first-post-button"
          >
            <FontAwesomeIcon icon={faPlus} />
            Create Your First Post
          </button>
        )}
      </div>
    );
  };



  // Header actions
  const headerActions = null; // Moved to filters section

  // Render posts grid
  const renderPostsGrid = () => (
    <div className="posts-grid">
      {sortedPosts.map(post => (
        <CommunityPostCard
          key={post.id}
          post={post}
          groupId={groupId}
          onPostDeleted={handlePostDeleted}
          onPostEdit={handlePostEdit}
        />
      ))}
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <AdminPageLayout
        title="Community Updates"
        icon={<FontAwesomeIcon icon={faNewspaper} />}
        subtitle="Share posts and build community connection."
        loading={true}
      />
    );
  }

  // Error state
  if (error && !hasApiError) {
    return (
      <AdminPageLayout
        title="Community Updates"
        icon={<FontAwesomeIcon icon={faNewspaper} />}
        subtitle="Share posts and build community connection."
        error={error}
      />
    );
  }

  // Main layout wrapper
  const LayoutWrapper = ({ children }) => {
    if (isAdmin) {
      return (
        <AdminPageLayout
          className="community-feed-seamless"
          title="Community Updates"
          icon={<FontAwesomeIcon icon={faNewspaper} />}
          subtitle="Share posts, announcements, and moments with your community."
          headerActions={headerActions}
          showFilters={isAdmin}
          filters={renderFilters()}
          loading={false}
          error={null}
          emptyState={sortedPosts.length === 0 ? renderEmptyState() : null}
        >
          {children}
        </AdminPageLayout>
      );
    } else {
      // Member view
      return (
        <div className="member-updates-container">
          {sortedPosts.length === 0 ? renderEmptyState() : children}
        </div>
      );
    }
  };

  return (
    <>
      <LayoutWrapper>
        {sortedPosts.length > 0 && renderPostsGrid()}
      </LayoutWrapper>

      {/* Create Post Modal */}
      {console.log('🔍 Rendering modal area, showCreateModal:', showCreateModal)}
      {showCreateModal && (
        <CreatePostModal
          isOpen={showCreateModal}
          groupId={groupId}
          editingPost={editingPost}
          postTags={postTags}
          activeTag={activeTag}
          onClose={handleModalClose}
          onPostCreated={editingPost ? handlePostUpdated : handlePostCreated}
        />
      )}
      
      {/* Tag Management Modal */}
      {showTagModal && (
        <PostTagModal
          isOpen={showTagModal}
          onClose={() => {
            setShowTagModal(false);
            setEditingTag(null);
          }}
          groupId={groupId}
          editingTag={editingTag}
          onTagSaved={handleTagSaved}
        />
      )}
      
      {/* Delete Tag Confirmation Modal */}
      {showDeleteTagModal && (
        <DeleteConfirmationModal
          isOpen={showDeleteTagModal}
          onClose={() => {
            setShowDeleteTagModal(false);
            setDeletingTag(null);
          }}
          onConfirm={handleDeleteTag}
          title="Delete Tag"
          message={`Are you sure you want to delete the "${deletingTag?.name}" tag? Posts with this tag will not be deleted, but will need to be re-tagged.`}
        />
      )}
    </>
  );
};

export default UpdatesList;
